import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import { useMemo } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import type { SlimPostWithTopics } from '../post/useAllSlimPosts';
import useAllSlimPosts from '../post/useAllSlimPosts';

export type LibraryTemplate = {
  title: string;
  link: string;
  libraryId: string;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const LIBRARY: LibraryTemplate[] = [
  {
    title: '1:1 Agenda - Asana',
    link: 'https://go.slab.com/slab-library-asana-one-on-one-meeting-agenda',
    libraryId: 'asana-one-on-one-meeting-agenda',
  },
  {
    title: 'Job Story Template - Intercom',
    link: 'https://go.slab.com/slab-library-intercom-job-story',
    libraryId: 'intercom-job-story',
  },
  {
    title: 'Culture - Netflix',
    link: 'https://go.slab.com/slab-library-netflix-culture',
    libraryId: 'netflix-culture',
  },
  {
    title: 'Code Review Developer Guide - Google',
    link: 'https://go.slab.com/slab-library-google-code-review',
    libraryId: 'google-code-review',
  },
  {
    title: 'RFC Template - Squarespace',
    link: 'https://go.slab.com/slab-library-squarespace-rfc',
    libraryId: 'squarespace-rfc-template',
  },
  {
    title: 'Postmortem Template - PagerDuty',
    link: 'https://go.slab.com/slab-library-pagerduty-postmortem',
    libraryId: 'pagerduty-postmortem-template',
  },
];

const TEMPLATES_LIST_QUERY = gql(/** @complexity 4 */ `
  query TemplatesSections {
    session: currentSession {
      user {
        id
        membershipTopics(includeGroups: true) {
          id
        }
      }
    }
  }
`);

const sortTemplates = (templates: SlimPostWithTopics[]) =>
  _.compact(
    templates.map((template) => {
      const topic = template.topics[0];
      return topic
        ? {
            template,
            path: [
              template.title,
              topic.name,
              ...topic.ancestors.map((ancestor) => ancestor.name),
            ].toReversed(), // From the highest ancestor to the topic itself
          }
        : null;
    }),
  )
    .toSorted(
      (a, b) =>
        a.path.length !== b.path.length
          ? a.path.length - b.path.length // Prefer higher level (fewer ancestors) topics
          : a.path.join('').localeCompare(b.path.join('')), // Same level sort alphabetically
    )
    .map((item) => item.template);

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const getTemplateSections = ({
  membershipTopicIdsIds,
  recentlyUsedTemplateIds,
  allSlimPosts,
  currentTopicId,
  sectionLimit,
  totalLimit,
}: {
  recentlyUsedTemplateIds: string[];
  membershipTopicIdsIds: string[];
  allSlimPosts: SlimPostWithTopics[];
  currentTopicId?: string;
  sectionLimit?: number;
  totalLimit?: number;
}) => {
  const recentlyUsedTemplateIdsSet = new Set(recentlyUsedTemplateIds);

  const myTopicsIdSet = new Set(membershipTopicIdsIds);

  const grouped = allSlimPosts.reduce(
    (acc, post) => {
      if (
        !post.publishedAt ||
        post.archivedAt ||
        post.type !== PostType.Template
      ) {
        return acc;
      }
      if (recentlyUsedTemplateIdsSet.has(post.id)) {
        acc.recentlyUsed[post.id] = post;
        return acc;
      }
      if (post.topics.length === 0) {
        return acc;
      }
      if (
        currentTopicId &&
        post.topics.some(
          (topic) =>
            topic.id === currentTopicId ||
            topic.ancestors.some((ancestor) => ancestor.id === currentTopicId),
        )
      ) {
        acc.currentTopic.push(post);
        return acc;
      }
      if (post.topics.some((topic) => myTopicsIdSet.has(topic.id))) {
        acc.myTopics.push(post);
        return acc;
      }
      acc.allTopics.push(post);
      return acc;
    },
    {
      recentlyUsed: {} as Record<string, SlimPostWithTopics>,
      currentTopic: [] as SlimPostWithTopics[],
      myTopics: [] as SlimPostWithTopics[],
      allTopics: [] as SlimPostWithTopics[],
    },
  );

  const sections = [
    {
      title: 'Recently used',
      templates: _.compact(
        recentlyUsedTemplateIds.map((id) => grouped.recentlyUsed[id]),
      ).slice(0, sectionLimit),
    },
    {
      title: 'Current topic',
      templates: sortTemplates(grouped.currentTopic).slice(0, sectionLimit),
    },
    {
      title: 'My topics',
      templates: sortTemplates(grouped.myTopics).slice(0, sectionLimit),
    },
    {
      title: 'All topics',
      templates: sortTemplates(grouped.allTopics).slice(0, sectionLimit),
    },
    { title: 'Slab Library', templates: LIBRARY },
  ] as const;

  if (totalLimit) {
    let counter = 0;
    return sections
      .map((section) => {
        const left = totalLimit - counter;
        if (left <= 0) {
          return { ...section, templates: [] };
        }

        const templates = section.templates.slice(0, left);
        counter += templates.length ?? 0;

        return { ...section, templates };
      })
      .filter((section) => section.templates.length > 0);
  }

  return sections.filter((section) => section.templates.length > 0);
};

const useTemplatesSections = ({
  currentTopicId,
  totalLimit,
  sectionLimit,
  skip,
}: {
  currentTopicId?: string;
  totalLimit?: number;
  sectionLimit?: number;
  skip?: boolean;
} = {}) => {
  const recentlyUsedTemplateIds = useAppSelector(
    (state) => state.ui.recentlyUsedTemplateIds,
  );

  const allSlimPosts = useAllSlimPosts();

  const { data } = useQuery(TEMPLATES_LIST_QUERY, { skip });

  return useMemo(
    () =>
      getTemplateSections({
        recentlyUsedTemplateIds,
        membershipTopicIdsIds:
          data?.session.user.membershipTopics.map((topic) => topic.id) ?? [],
        allSlimPosts,
        currentTopicId,
        sectionLimit,
        totalLimit,
      }),
    [
      allSlimPosts,
      currentTopicId,
      data?.session.user.membershipTopics,
      recentlyUsedTemplateIds,
      totalLimit,
      sectionLimit,
    ],
  );
};

export default useTemplatesSections;
